import { stripPartialIssueDraftFields } from './zonePartialIssueUtils';

const toZoneQty = (value) => {
  const parsed = Number.parseInt(String(value ?? 0), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getIssueRowChangeKey = (row) =>
  String(row?.stock_id ?? row?.id ?? '');

export const getRowPoProductId = (row) => {
  const id = row?.po_product_id ?? row?.id ?? row?.product_id;
  if (id === null || id === undefined || id === '') {
    return null;
  }
  return String(id);
};

export const getIssueChangeRowMeta = (row) => ({
  rowId: getRowPoProductId(row) || String(row?.id ?? ''),
  stockId: String(row?.stock_id ?? ''),
  SKU: String(row?.SKU ?? row?.sku ?? '-'),
});

const getSessionChangeEntryRowId = (entry) =>
  String(entry?.rowId ?? entry?.id ?? '');

const mergeEventIntoSessionEvents = (events, newEvent) => {
  const type = newEvent?.type;
  const addQty = toZoneQty(newEvent?.qty);
  if (!type || addQty <= 0) {
    return events || [];
  }

  const next = [...(events || [])];
  const existingIndex = next.findIndex((event) => event?.type === type);

  if (existingIndex >= 0) {
    next[existingIndex] = {
      type,
      qty: toZoneQty(next[existingIndex].qty) + addQty,
    };
    return next;
  }

  return [...next, { type, qty: addQty }];
};

export const mergeSessionChangeEventsByType = (events = []) => {
  const merged = [];

  (events || []).forEach((event) => {
    const type = event?.type;
    const qty = toZoneQty(event?.qty);
    if (!type || qty <= 0) {
      return;
    }

    const existingIndex = merged.findIndex((entry) => entry.type === type);
    if (existingIndex >= 0) {
      merged[existingIndex] = {
        type,
        qty: toZoneQty(merged[existingIndex].qty) + qty,
      };
      return;
    }

    merged.push({ type, qty });
  });

  return merged;
};

const getRowSessionIssuedQty = (row) => {
  if (row?._changeSnapshot != null) {
    const prevIssue = toZoneQty(row._changeSnapshot.issue);
    return Math.max(0, toZoneQty(row?.issue) - prevIssue);
  }

  if (row?._issueDraftActive) {
    return toZoneQty(row._issueNow);
  }

  return toZoneQty(row?.issue);
};

export const findPreviousIssueRowMatch = (row, previousRows = []) =>
  (previousRows || []).find((prev) => {
    const stockId = String(row?.stock_id ?? '');
    const rowId = String(row?.id ?? '');
    return (
      (stockId && String(prev?.stock_id ?? '') === stockId) ||
      (rowId && String(prev?.id ?? '') === rowId)
    );
  });

export const buildIssueChangeEventFromRowDiff = (previousRow, nextRow) => {
  if (!nextRow) {
    return null;
  }

  const prevIssue = toZoneQty(previousRow?.issue);
  const nextIssue = toZoneQty(nextRow?.issue);
  const prevReturn = toZoneQty(previousRow?.return ?? previousRow?.Return);
  const nextReturn = toZoneQty(nextRow?.return ?? nextRow?.Return);

  if (nextReturn > prevReturn) {
    return { type: 'returned', qty: nextReturn - prevReturn };
  }

  if (nextIssue > prevIssue) {
    return { type: 'issued', qty: nextIssue - prevIssue };
  }

  if (!previousRow && nextIssue > 0) {
    return { type: 'issued', qty: nextIssue };
  }

  return null;
};

export const appendSessionIssueChangeEvent = (sessionChanges, row, event) => {
  const meta = getIssueChangeRowMeta(row);
  const qty = toZoneQty(event?.qty);
  if (!meta.rowId || !event?.type || qty <= 0) {
    return sessionChanges || [];
  }

  const cleanEvent = { type: event.type, qty };
  const existingIndex = (sessionChanges || []).findIndex(
    (entry) => getSessionChangeEntryRowId(entry) === String(meta.rowId)
  );

  if (existingIndex >= 0) {
    const next = [...sessionChanges];
    next[existingIndex] = {
      ...next[existingIndex],
      stockId: meta.stockId || next[existingIndex].stockId,
      SKU: meta.SKU || next[existingIndex].SKU,
      events: mergeEventIntoSessionEvents(
        next[existingIndex].events || [],
        cleanEvent
      ),
    };
    return next;
  }

  return [
    ...(sessionChanges || []),
    {
      rowId: meta.rowId,
      stockId: meta.stockId,
      SKU: meta.SKU,
      events: [cleanEvent],
    },
  ];
};

export const collectSessionIssueChangeEvents = (
  sessionChanges,
  previousRows,
  nextRows
) => {
  let updated = sessionChanges || [];

  (nextRows || []).forEach((nextRow) => {
    const previousRow = findPreviousIssueRowMatch(nextRow, previousRows);
    const prevIssue = toZoneQty(previousRow?.issue);
    const nextIssue = toZoneQty(nextRow?.issue);
    const prevReturn = toZoneQty(previousRow?.return ?? previousRow?.Return);
    const nextReturn = toZoneQty(nextRow?.return ?? nextRow?.Return);

    if (
      previousRow &&
      prevIssue === nextIssue &&
      prevReturn === nextReturn
    ) {
      return;
    }

    const event = buildIssueChangeEventFromRowDiff(previousRow, nextRow);
    if (!event) {
      return;
    }

    updated = appendSessionIssueChangeEvent(updated, nextRow, event);
  });

  return updated;
};

export const revertSessionIssueChangeEvent = (
  sessionChanges,
  rowId,
  eventType
) =>
  (sessionChanges || [])
    .map((entry) => {
      if (getSessionChangeEntryRowId(entry) !== String(rowId ?? '')) {
        return entry;
      }

      const events = [...(entry.events || [])];
      for (let index = events.length - 1; index >= 0; index -= 1) {
        if (events[index]?.type === eventType) {
          events.splice(index, 1);
          break;
        }
      }

      return { ...entry, events };
    })
    .filter((entry) => entry?.events?.length);

export const syncSessionIssuedEventQty = (sessionChanges, row) => {
  const meta = getIssueChangeRowMeta(row);
  const qty = getRowSessionIssuedQty(row);
  if (!meta.rowId || qty <= 0) {
    return sessionChanges || [];
  }

  const entryRowId = String(meta.rowId);
  const existingIndex = (sessionChanges || []).findIndex(
    (entry) => getSessionChangeEntryRowId(entry) === entryRowId
  );

  if (existingIndex >= 0) {
    const next = [...sessionChanges];
    const events = [...(next[existingIndex].events || [])];
    let updated = false;

    for (let index = events.length - 1; index >= 0; index -= 1) {
      if (events[index]?.type === 'issued') {
        events[index] = { type: 'issued', qty };
        updated = true;
        break;
      }
    }

    if (!updated) {
      events.push({ type: 'issued', qty });
    }

    next[existingIndex] = { ...next[existingIndex], events };
    return next;
  }

  return appendSessionIssueChangeEvent(sessionChanges || [], row, {
    type: 'issued',
    qty,
  });
};

export const removeSessionIssueChangeRow = (sessionChanges, rowId) =>
  (sessionChanges || []).filter(
    (entry) => getSessionChangeEntryRowId(entry) !== String(rowId ?? '')
  );

/** Payload for save API — events have type/qty only; backend adds timestamps. */
export const buildSessionIssueChangesPayload = (
  sessionIssueChanges = [],
  rows = [],
  baselineMap = {}
) => {
  const payloadById = new Map();

  (sessionIssueChanges || []).forEach((entry) => {
    const entryRowId = getSessionChangeEntryRowId(entry);
    if (!entryRowId || !entry?.events?.length) {
      return;
    }

    payloadById.set(entryRowId, {
      rowId: entryRowId,
      stockId: entry.stockId,
      SKU: entry.SKU,
      events: mergeSessionChangeEventsByType(entry.events || []),
    });
  });

  (rows || []).forEach((row) => {
    if (!row?.new_add && !row?.new_return) {
      return;
    }

    const meta = getIssueChangeRowMeta(row);
    if (!meta.rowId || payloadById.has(String(meta.rowId))) {
      return;
    }

    const key = getIssueRowChangeKey(row);
    const baseline = baselineMap?.[key];
    const snap = row._changeSnapshot || {};
    const events = [];

    if (row.new_return) {
      const prevReturn = toZoneQty(snap.return ?? baseline?.return);
      const nextReturn = toZoneQty(row?.return);
      const returnDelta = nextReturn - prevReturn;
      if (returnDelta > 0) {
        events.push({ type: 'returned', qty: returnDelta });
      }
    }

    if (row.new_add) {
      const prevIssue = toZoneQty(snap.issue ?? baseline?.issue ?? 0);
      const nextIssue = toZoneQty(row?.issue);
      const hasPriorIssue = Boolean(baseline) || snap.issue != null;
      const issueDelta = row._issueDraftActive
        ? toZoneQty(row._issueNow)
        : hasPriorIssue
          ? nextIssue - prevIssue
          : nextIssue;
      if (issueDelta > 0) {
        events.push({ type: 'issued', qty: issueDelta });
      }
    }

    if (!events.length) {
      return;
    }

    payloadById.set(String(meta.rowId), {
      rowId: meta.rowId,
      stockId: meta.stockId,
      SKU: meta.SKU,
      events: mergeSessionChangeEventsByType(events),
    });
  });

  return Array.from(payloadById.values()).map((entry) => ({
    ...entry,
    events: mergeSessionChangeEventsByType(entry.events || []),
  }));
};

export const isSessionOnlyIssueRow = (row, viewEditIssueId, baselineMap = {}) => {
  if (!viewEditIssueId) {
    return true;
  }
  const key = getIssueRowChangeKey(row);
  if (!key) {
    return true;
  }
  return !baselineMap?.[key];
};

export const buildIssueBaselineMap = (rows = []) => {
  const map = {};
  (rows || []).forEach((row) => {
    const key = getIssueRowChangeKey(row);
    if (!key) return;
    map[key] = {
      issue: row.issue ?? row.Issue ?? 0,
      return: row.return ?? row.Return ?? 0,
      balance: row.balance ?? row.Balance ?? 0,
    };
  });
  return map;
};

export const buildDeletedIssueChangeEntry = (row, { wasNewAdd = false } = {}) => ({
  key: getIssueRowChangeKey(row),
  rowId: row?.id,
  stockId: row?.stock_id || '-',
  SKU: row?.SKU || '-',
  type: 'deleted',
  wasNewAdd: Boolean(wasNewAdd),
});

export const getIssueChangeEntries = (
  rows = [],
  deletedEntries = [],
  baselineMap = {}
) => {
  const activeChanges = (rows || [])
    .filter((row) => row?.new_add || row?.new_return)
    .map((row) => {
      const key = getIssueRowChangeKey(row);
      const isIssued = Boolean(row.new_add);
      const isAlreadyIssuedRow = Boolean(baselineMap?.[key]);
      return {
        key,
        rowId: row.id,
        stockId: row.stock_id || '-',
        SKU: row.SKU || '-',
        types: [isIssued ? 'issued' : 'returned'],
        changeSnapshot: row._changeSnapshot || null,
        isNewRow: !isAlreadyIssuedRow,
      };
    });

  const activeKeys = new Set(activeChanges.map((entry) => entry.key));
  const deletedChanges = [];

  (deletedEntries || []).forEach((entry) => {
    if (!entry?.key || activeKeys.has(entry.key)) return;

    deletedChanges.push({
      key: entry.key,
      rowId: entry.rowId,
      stockId: entry.stockId,
      SKU: entry.SKU,
      types: entry.wasNewAdd ? ['issued', 'deleted'] : ['deleted'],
      noRevert: true,
      isNewRow: Boolean(entry.wasNewAdd),
    });
  });

  return [...activeChanges, ...deletedChanges];
};

export const isRevertibleIssueChange = (change) => {
  const types = change?.types || [];
  const primaryType = types[0] || 'issued';
  return (
    !change?.noRevert &&
    types.length === 1 &&
    primaryType !== 'deleted'
  );
};

export const revertAllIssueChangesFromRows = (rows = [], baselineMap = {}) => {
  const changeRows = (rows || []).filter((row) => row?.new_add || row?.new_return);
  let updated = rows || [];

  changeRows.forEach((row) => {
    const key = getIssueRowChangeKey(row);
    if (row.new_add) {
      updated = revertIssuedRowFromRows(updated, key, baselineMap);
    } else if (row.new_return) {
      updated = revertReturnedRowFromRows(updated, key, baselineMap);
    }
  });

  return updated;
};

export const revertIssuedRowFromRows = (rows, changeKey, baselineMap = {}) => {
  const hasBaseline = Boolean(baselineMap?.[changeKey]);

  if (!hasBaseline) {
    return (rows || []).filter((row) => getIssueRowChangeKey(row) !== changeKey);
  }

  return (rows || []).map((row) => {
    if (getIssueRowChangeKey(row) !== changeKey) return row;

    const snap = row._changeSnapshot || baselineMap[changeKey];
    const nextRow = stripPartialIssueDraftFields({ ...row });
    delete nextRow.new_add;
    delete nextRow._changeSnapshot;

    return {
      ...nextRow,
      issue: snap?.issue ?? nextRow.issue,
      return: snap?.return ?? nextRow.return,
      balance: snap?.balance ?? nextRow.balance,
    };
  });
};

export const revertReturnedRowFromRows = (rows, changeKey, baselineMap = {}) => {
  return (rows || []).map((row) => {
    if (getIssueRowChangeKey(row) !== changeKey) return row;
    if (!row.new_return) return row;

    const snap = row._changeSnapshot || baselineMap?.[changeKey];
    if (!snap) return row;

    const nextRow = stripPartialIssueDraftFields({ ...row });
    delete nextRow.new_return;
    delete nextRow._changeSnapshot;

    return {
      ...nextRow,
      return: snap.return ?? nextRow.return,
      balance: snap.balance ?? nextRow.balance,
    };
  });
};
